/**
 * Sidebar
 *
 * Left sidebar — always visible on desktop.
 * Brand: JAI Lean Dial mark + Inter Tight wordmark, navy palette.
 * No emoji in chrome — SVG icons only.
 */

import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useLeagues } from '../context/LeagueContext'
import { useAuth } from '../context/AuthContext'
import AddLeagueModal from './AddLeagueModal'
import RemoveLeagueModal from './RemoveLeagueModal'
import AccountMenu from './AccountMenu'
import GuestPrompt from './GuestPrompt'
import { JaiMark, JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { IS_NATIVE_APP } from '../services/runtime'
import { APP_STORE_URL } from '../config/appStore'

// ── Sidebar type + colour scale ──────────────────────────────────────────────
// The nav reads off a cool off-white (JAI "paper", the same tone as the Ask JAI
// background) rather than plain grey, so it looks like brand chrome instead of
// dimmed-out text.
//
// Deliberately only TWO tones. A ladder of near-misses (0.82 / 0.75 / 0.70…)
// doesn't read as hierarchy — it reads as text that failed to render the same
// colour twice. Rank is carried by size, weight and case instead.

const TEXT = {
  primary: JAI_COLORS.paper,          // anything you can click: Ask JAI, leagues, Add League, section label
  muted:   'rgba(246,248,251,0.68)',  // the data underneath: "10 teams · 2025", empty state
}

// ── SVG icon set ─────────────────────────────────────────────────────────────
// Nav icons render at 20px (viewBox stays 16, so strokes scale up with them) —
// big enough to read at a glance next to 15px labels.

const ICON = 20

const icons = {
  chat: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path d="M2 2h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5l-3 2V3a1 1 0 0 1 1-1Z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ),
  // Basketball. The corner arcs are what sell it — a plain cross inside a
  // circle reads as a globe, which is what the old league icon looked like.
  nba: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1.7v12.6M1.7 8h12.6" stroke="currentColor" strokeWidth="1.1" opacity="0.75" />
      <path d="M3.55 3.55a8.5 8.5 0 0 0 8.9 8.9M12.45 3.55a8.5 8.5 0 0 0-8.9 8.9"
        stroke="currentColor" strokeWidth="1.1" opacity="0.75" fill="none" />
    </svg>
  ),
  // American football. Three bare laces only: adding a centre line as well
  // merges them into a solid blob once the icon is down at 16px.
  nfl: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M1.8 8C1.8 8 4.6 2.5 8 2.5s6.2 5.5 6.2 5.5-2.8 5.5-6.2 5.5S1.8 8 1.8 8Z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M6.6 6.9v2.2M8 6.7v2.6M9.4 6.9v2.2"
        stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
    </svg>
  ),
  plus: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  // Three dots — the per-league menu handle. Vertical, so it reads as "more
  // options for this row" rather than as part of the league's own label.
  dots: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="3.2" r="1.35" fill="currentColor" />
      <circle cx="8" cy="8"   r="1.35" fill="currentColor" />
      <circle cx="8" cy="12.8" r="1.35" fill="currentColor" />
    </svg>
  ),
  // Apple logo — used only by the "Get the iOS app" link below.
  apple: (
    <svg width={ICON} height={ICON} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.54c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.42-.14-2.76.83-3.48.83-.71 0-1.82-.81-2.99-.79-1.54.02-2.96.89-3.75 2.26-1.6 2.77-.41 6.88 1.15 9.13.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.78.74 2.99.72 1.23-.02 2.02-1.12 2.78-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.64zM14.8 5.36c.63-.77 1.06-1.83.94-2.9-.91.04-2.01.61-2.66 1.37-.59.68-1.1 1.76-.96 2.8 1.01.08 2.05-.52 2.68-1.27z" />
    </svg>
  ),
}

// Picks the ball that matches the league's sport, so the sidebar shows at a
// glance which of a user's leagues is football and which is basketball.
// Basketball is the fallback for any league connected before JAI recorded the
// sport, since that was the only sport supported then.
const sportIcon = (sport) =>
  String(sport || '').toLowerCase() === 'nfl' ? icons.nfl : icons.nba

// ── Main component ────────────────────────────────────────────────────────────

const chevronLeft = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const chevronRight = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export default function Sidebar({ open = true, onToggle }) {
  const { leagues, addLeague, removeLeague } = useLeagues()
  const { refreshPlan, user } = useAuth()
  const [showAddModal,   setShowAddModal]   = useState(false)
  const [menuLeagueId,   setMenuLeagueId]   = useState(null)   // which row's "⋯" menu is open
  const [leagueToDelete, setLeagueToDelete] = useState(null)   // set once they pick Delete — opens the confirm dialog
  const [hoveredLeagueId, setHoveredLeagueId] = useState(null)
  const [focusedLeagueId, setFocusedLeagueId] = useState(null)
  const location  = useLocation()
  const navigate  = useNavigate()

  // The "⋯" handle sits quietly at low opacity and brightens when you're on the
  // row, have tabbed to it, or left its menu open. It stays visible rather than
  // appearing only on hover, so it's discoverable — and so it works on phones
  // and tablets, where there is no hover at all.
  const handleActive = (leagueId) =>
    hoveredLeagueId === leagueId || focusedLeagueId === leagueId || menuLeagueId === leagueId

  const handleLeagueAdded = (league) => {
    addLeague(league)
    setShowAddModal(false)
    navigate(`/league/${league.id}`)
  }

  // Coming back from Yahoo's login page, the browser lands here with
  // ?yahoo_pending=... in the URL. Reopen the Add League modal so it can pick
  // that up and show the league list — otherwise the user returns to a normal
  // page with no sign anything happened. See routes/yahoo.js on the backend.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('yahoo_pending') || params.get('yahoo_error')) {
      setShowAddModal(true)
    }
  }, [location.search])

  // Clicking anywhere else, or pressing Escape, closes an open row menu — so it
  // never sits open over the nav after you've moved on.
  useEffect(() => {
    if (!menuLeagueId) return
    const close = () => setMenuLeagueId(null)
    const onKey = (e) => { if (e.key === 'Escape') setMenuLeagueId(null) }
    window.addEventListener('click', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [menuLeagueId])

  const handleLeagueDeleted = async (leagueId) => {
    removeLeague(leagueId)
    setLeagueToDelete(null)
    await refreshPlan()
    // Only bail out of the current page if it belongs to the league just deleted.
    if (location.pathname.startsWith(`/league/${leagueId}`)) {
      navigate('/chat', { replace: true })
    }
  }

  return (
    <>
      {/* Floating re-open tab — only visible when sidebar is hidden */}
      {!open && (
        <button
          onClick={onToggle}
          title="Open sidebar"
          style={{
            // calc() with the safe-area var so this still sits 16px below
            // the true top of the content area even under a notch/Dynamic
            // Island, instead of 16px below the top of the whole screen
            // (which on a notched phone can be under the cutout itself).
            position: 'fixed', top: 'calc(var(--safe-top) + 16px)', left: 0, zIndex: 60,
            background: JAI_COLORS.navyDeep,
            border: '1px solid rgba(255,255,255,0.12)',
            borderLeft: 'none',
            borderRadius: '0 8px 8px 0',
            padding: '8px 10px',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            boxShadow: '2px 0 12px rgba(0,0,0,0.3)',
            transition: 'color 150ms',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'white'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
        >
          {chevronRight}
        </button>
      )}

      <aside
        className="w-64 border-r flex flex-col h-screen fixed left-0 top-0"
        style={{
          background: JAI_COLORS.navyDeep,
          borderColor: 'rgba(255,255,255,0.08)',
          boxShadow: '4px 0 32px rgba(0,0,0,0.4)',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
          zIndex: 50,
        }}
      >

        {/* ── Logo ─────────────────────────────────────────────── */}
        <div className="px-5 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Lockup proportions: the mark's circle fills ~90% of its box, so a
              34px mark sits at roughly 1.6× the wordmark's cap height — the mark
              still leads, but "JAI" now carries its share instead of trailing it. */}
          <Link to="/chat" className="flex items-center gap-2.5 group">
            <JaiMark size={34} bg={JAI_COLORS.blue} accent={JAI_COLORS.blueBright} />
            <div style={{ fontFamily: JAI_FONTS.tight, fontWeight: 800, fontSize: 25, letterSpacing: '-0.045em', color: 'white', lineHeight: 1 }}>
              JAI
            </div>
          </Link>
          <button
            onClick={onToggle}
            title="Collapse sidebar"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 150ms, background 150ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent' }}
          >
            {chevronLeft}
          </button>
        </div>

        {/* ── Main nav ─────────────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">

          <NavItem
            to="/chat"
            icon={icons.chat}
            label="Ask JAI"
            active={location.pathname === '/chat'}
          />

          <div className="pt-5 pb-2 px-2">
            {/* Same colour as the rows below it — uppercase + tracking already
                mark it as a label, so it doesn't need to be dimmed too. Weight
                700 rather than 800: extra ink on a dark background reads as a
                brighter colour, which is exactly the unevenness to avoid. */}
            <span style={{ fontFamily: JAI_FONTS.tight, fontSize: 13, fontWeight: 700, letterSpacing: '0.085em', textTransform: 'uppercase', color: TEXT.primary }}>
              My Leagues
            </span>
          </div>

          {leagues.length === 0 ? (
            <p className="px-2 py-1" style={{ fontSize: 14, color: TEXT.muted }}>No leagues yet</p>
          ) : (
            leagues.map(league => (
              <div
                key={league.id}
                style={{ position: 'relative' }}
                onMouseEnter={() => setHoveredLeagueId(league.id)}
                onMouseLeave={() => setHoveredLeagueId(null)}
              >
                <NavItem
                  to={`/league/${league.id}`}
                  icon={sportIcon(league.sport)}
                  label={league.name}
                  sublabel={`${league.teamCount || '?'} teams · ${league.season}`}
                  active={location.pathname === `/league/${league.id}`}
                  trailingSpace
                />

                {/* Per-league menu handle. Sits outside the NavItem link (a
                    button nested in a link would navigate on click). */}
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()   // don't trip the close-on-outside-click listener
                    setMenuLeagueId(menuLeagueId === league.id ? null : league.id)
                  }}
                  onFocus={() => setFocusedLeagueId(league.id)}
                  onBlur={() => setFocusedLeagueId(null)}
                  title="League options"
                  aria-label={`Options for ${league.name}`}
                  aria-haspopup="menu"
                  aria-expanded={menuLeagueId === league.id}
                  style={{
                    position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none', borderRadius: 6,
                    color: 'white', cursor: 'pointer',
                    padding: 3, display: 'flex', alignItems: 'center',
                    opacity: handleActive(league.id) ? 1 : 0.4,
                    transition: 'opacity 150ms, background 150ms',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  {icons.dots}
                </button>

                {menuLeagueId === league.id && (
                  <div
                    role="menu"
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: 'absolute', right: 4, top: 'calc(100% - 2px)', zIndex: 70,
                      background: '#0F2549', border: '1px solid rgba(255,255,255,0.14)',
                      borderRadius: 10, padding: 4, minWidth: 148,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    }}
                  >
                    <button
                      role="menuitem"
                      onClick={() => { setMenuLeagueId(null); setLeagueToDelete(league) }}
                      style={{
                        width: '100%', textAlign: 'left', background: 'transparent',
                        border: 'none', borderRadius: 7, padding: '7px 10px',
                        fontSize: 13, fontWeight: 500, color: '#FF6B6B',
                        cursor: 'pointer', fontFamily: JAI_FONTS.tight,
                        transition: 'background 150ms',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,107,0.14)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      Delete league
                    </button>
                  </div>
                )}
              </div>
            ))
          )}

          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition"
            style={{ fontSize: 15, color: TEXT.primary }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'white' }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TEXT.primary }}
          >
            <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              {icons.plus}
            </span>
            <span style={{ fontWeight: 600 }}>Add League</span>
          </button>
        </nav>

        {/* ── Bottom: guest prompt + iOS app link + account menu ───── */}
        {/* GuestPrompt renders nothing for a signed-up user, so this block
            looks exactly as it always did once the account is claimed.

            The App Store link is web-only, explicitly — this Sidebar never
            mounts inside the native app anyway (AppShell.jsx renders it only
            when !IS_NATIVE_APP, and BottomTabBar is native's equivalent nav),
            but someone already inside the iOS app has no use for a link back
            to it, so this guards the same way regardless of where Sidebar is
            ever rendered from. See src/config/appStore.js for the link.

            Also hidden once user.hasUsedIosApp is true — set server-side the
            first time this account makes an authenticated request FROM the
            native app (authMiddleware.js). There's no way for a website to
            ask a phone "is this app installed?", so this is a proxy instead:
            not "installed", but "this account already uses the app". */}
        <div className="px-3 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <GuestPrompt />
          {!IS_NATIVE_APP && !user?.hasUsedIosApp && (
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition"
              style={{ fontSize: 13.5, color: TEXT.muted, textDecoration: 'none', marginBottom: 4 }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = TEXT.primary }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TEXT.muted }}
            >
              <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                {icons.apple}
              </span>
              <span style={{ fontWeight: 600 }}>Get the iOS app</span>
            </a>
          )}
          <AccountMenu />
        </div>
      </aside>

      {showAddModal && (
        <AddLeagueModal
          onClose={() => setShowAddModal(false)}
          onAdded={handleLeagueAdded}
        />
      )}

      {leagueToDelete && (
        <RemoveLeagueModal
          league={leagueToDelete}
          onClose={() => setLeagueToDelete(null)}
          onRemoved={() => handleLeagueDeleted(leagueToDelete.id)}
        />
      )}
    </>
  )
}

// ── NavItem ───────────────────────────────────────────────────────────────────

function NavItem({ to, icon, label, sublabel, active, trailingSpace = false }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 px-2 py-2 rounded-lg transition group"
      style={{
        backgroundColor: 'transparent',
        color: TEXT.primary,
        border: '1px solid transparent',
        boxShadow: active ? 'inset 3px 0 0 #3A7CFF' : 'none',
        borderRadius: active ? 0 : undefined,
        // Keeps long league names from running under the "⋯" button
        paddingRight: trailingSpace ? 28 : undefined,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent' }}
    >
      {/* No opacity on the icon — it inherits currentColor, so it lands on
          exactly the same tone as the label sitting next to it. */}
      <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="truncate leading-tight" style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.005em' }}>{label}</div>
        {sublabel && (
          <div className="truncate leading-tight mt-0.5"
            style={{ fontSize: 13, color: TEXT.muted, fontWeight: 500 }}>
            {sublabel}
          </div>
        )}
      </div>
    </Link>
  )
}
